/**
 * An error that reaches a log is untrusted input. `name` is writable, so it is
 * matched against an allow-list rather than copied; `message` is free text an
 * HTTP client will happily fill with the payload it failed on — the customer's
 * name, email and phone on a checkout form — so it is redacted before it goes
 * anywhere. Why a request failed is a different question, owned by the api
 * client's `classifyFetchFailure`.
 */

/**
 * Exported so the class that sets it and the allow-list that recognises it
 * cannot drift apart, which would report every address failure as
 * `unrecognised`.
 */
export const PLACES_ROUTE_ERROR_NAME = "PlacesRouteError";

/** Standard platform error names, plus the ones our own stack throws. */
const ALLOWED_ERROR_NAMES = new Map(
    [
        "Error",
        "TypeError",
        "RangeError",
        "ReferenceError",
        "SyntaxError",
        "EvalError",
        "URIError",
        "AbortError",
        "TimeoutError",
        "NetworkError",
        "NotAllowedError",
        "SecurityError",
        "QuotaExceededError",
        "ZodError",
        PLACES_ROUTE_ERROR_NAME,
    ].map((name) => [name.toLowerCase(), name])
);

/**
 * Stands in for a name that is not on the list. A constant rather than
 * `undefined` so the field is always present and a spike in unrecognised
 * throws is visible instead of looking like an absence of throws.
 */
export const UNRECOGNISED_ERROR_NAME = "unrecognised";

/** Reported when the value thrown was not an error object at all. */
export const NON_ERROR_THROWN_NAME = "non_error";

/**
 * `instanceof` is realm-local, and the card fields run in a Chargebee iframe —
 * so an `Error` thrown there fails it and logs as "[object Error]".
 */
function isError(value: unknown): value is Error {
    return (
        value instanceof Error ||
        Object.prototype.toString.call(value) === "[object Error]"
    );
}

export function allowlistedErrorName(error: unknown): string {
    // Guarded: this runs in catch blocks that still owe the customer a working
    // page, and `name` may be an accessor that throws rather than answers.
    try {
        if (!isError(error)) return NON_ERROR_THROWN_NAME;

        const name = error.name;
        if (typeof name !== "string") return UNRECOGNISED_ERROR_NAME;

        return ALLOWED_ERROR_NAMES.get(name.toLowerCase()) ?? UNRECOGNISED_ERROR_NAME;
    } catch {
        return UNRECOGNISED_ERROR_NAME;
    }
}

export const REDACTED_EMAIL = "[email]";
export const REDACTED_NUMBER = "[number]";

/** A 422 that arrived with no `message` at all — a real response, not a bug. */
export const NO_MESSAGE = "[no message]";

/**
 * A local subscriber number is seven digits, and every number our server prose
 * legitimately contains — an amount, "redeemed 1 times", "2-letter" — is
 * shorter.
 */
const MIN_IDENTIFYING_DIGITS = 7;

/**
 * Everything numeric in one pass, ordered so amounts are claimed before the
 * greedy run reaches them. Two regexes would collapse "100.00 - 200.00
 * (300.00)" into a single match and spare the phone number in
 * "100.00 5551234567".
 *
 * Group 1 is an amount, passed through untouched: the one number worth reading
 * and the one that identifies nobody. Its bounds are what stop the exemption
 * being a smuggling route — the trailing guard rejects `.`, `,` and `/` so
 * "12.03.1985" is not read as $12.03, and the integer part is capped so
 * "5551234567.89" is not waved through on two decimal places. The grouped
 * branch is strict US currency, because mixing the separators would exempt
 * "123,456,78" on its trailing pair.
 *
 * The second alternative over-matches on purpose and `MIN_IDENTIFYING_DIGITS`
 * decides. It spans comma, bracket and slash, or "1,234,567" and "03/12/1985"
 * are never reached. Dates are deliberately not exempt: a birth date is an HHS
 * identifier here, and nothing tells one apart from a coupon expiry.
 */
const NUMERIC =
    /((?:\d{1,3}(?:,\d{3})+\.|\d{1,5}[.,])\d{2}(?![\d.,/]))|\+?\(?\d[\d\s(),./-]{5,}\d/g;

/**
 * Letters in any script, not `[a-z]`: an ASCII-only tail waved whole
 * internationalized addresses through on their country code. Marks count too —
 * ".भारत" is one letter and a mark, short of the two a TLD needs.
 *
 * The two bounds are the RFC 5321 limits, and they are load-bearing rather
 * than documentation. Unbounded, both runs also accept `@` and `.`, so the
 * engine tries every split for the `@` and every split for the `\.`, then
 * repeats from the next position when the address turns out not to be one:
 * quadratic, and measurably so — 2.5 seconds of blocked main thread on a 32 KB
 * input. What reaches here is prose a server wrote, whose length is not ours
 * to promise. Capping the runs makes the work per position constant; no
 * address a mail system would accept is excluded by it.
 */
const EMAIL =
    /[^\s<>()[\]{},;:"']{1,64}@[^\s<>()[\]{},;:"']{1,255}\.\p{L}[\p{L}\p{M}]+/giu;

/**
 * Strips customer identifiers out of a server-authored message so it can be
 * logged. Our API quotes the offending value back in one free-text `message`
 * ("Phone number is not valid 1 5551234567" is a real one), which Error
 * Tracking then retains and alerts on.
 *
 * A narrowing, not a guarantee: it takes the two mechanically recognisable
 * shapes — an `@` address and a long enough digit run — and cannot touch a
 * name or a street. A caller-controlled field still beats prose someone else
 * wrote.
 */
export function redactPersonalData(text: unknown): string {
    // `unknown` because a 422 with no `message` is a real response.
    if (text === null || text === undefined || text === "") return NO_MESSAGE;

    const source = typeof text === "string" ? text : String(text);

    return source
        .replace(EMAIL, REDACTED_EMAIL)
        .replace(NUMERIC, (match, preserved: string | undefined) => {
            if (preserved) return match;

            const digitCount = match.replace(/\D/g, "").length;
            return digitCount >= MIN_IDENTIFYING_DIGITS
                ? REDACTED_NUMBER
                : match;
        });
}

/**
 * A caught error flattened for a log payload, both halves laundered. `message`
 * and `stack` are non-enumerable, so an `Error` handed to Datadog serializes
 * to `{}` — which is why every catch block writes this pair out by hand, and
 * why the hand-rolled version is the unredacted one.
 */
export function describeError(error: unknown): {
    errorName: string;
    errorMessage: string;
} {
    const errorName = allowlistedErrorName(error);

    // Guarded like `allowlistedErrorName`, and for the same reason: reading or
    // rendering a value we did not create can throw instead of answering, and
    // this runs in catch blocks on the payment path where that leaves the
    // customer on a spinner nothing clears.
    try {
        return {
            errorName,
            errorMessage: redactPersonalData(
                isError(error) ? error.message : error
            ),
        };
    } catch {
        return { errorName, errorMessage: NO_MESSAGE };
    }
}
